/**
 * Color utility functions for ensuring accessibility and proper contrast
 */

/**
 * Convert hex color to RGB values
 * @param hex - Hex color string (e.g., '#FF0000' or 'FF0000')
 * @returns RGB values as an object
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Handle 3-digit hex codes
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return { r, g, b };
  }
  
  // Handle 6-digit hex codes
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return { r, g, b };
  }
  
  return null;
}

/**
 * Calculate relative luminance for a color
 * Based on WCAG 2.0 guidelines
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Relative luminance (0-1)
 */
export function getLuminance(r: number, g: number, b: number): number {
  // Convert to 0-1 range
  const [rs, gs, bs] = [r, g, b].map(val => {
    const normalized = val / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  
  // Calculate luminance using WCAG formula
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 * @param luminance1 - Luminance of first color
 * @param luminance2 - Luminance of second color
 * @returns Contrast ratio (1-21)
 */
export function getContrastRatio(luminance1: number, luminance2: number): number {
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine if white or black text reads better on a given background.
 *
 * Uses YIQ perceived brightness (the same model used elsewhere in the app for
 * brand-color contrast) rather than strict WCAG luminance. This matches design
 * intent for saturated brand colors — e.g. teal/blue/red brand backgrounds get
 * white text, while pale/yellow backgrounds get black text. Strict WCAG would
 * incorrectly prefer black on many medium-saturation brand colors.
 *
 * @param backgroundColor - Background color as hex string
 * @returns 'white' or 'black' for readable text color
 */
export function getContrastText(backgroundColor: string): 'white' | 'black' {
  const rgb = hexToRgb(backgroundColor);

  if (!rgb) {
    return backgroundColor.toLowerCase().includes('fff') ? 'black' : 'white';
  }

  // YIQ perceived brightness, threshold tuned so saturated/dark brand colors
  // (teals, blues, greens, reds) render white text while pale colors (yellow,
  // pastels, white) render dark text.
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness < 150 ? 'white' : 'black';
}

/**
 * Make a brand color safe to use as text/foreground on a white background.
 *
 * Very light brand colors (pastels, near-white) are illegible as text on
 * white, so darken them until they reach reasonable perceived contrast.
 * Colors that are already dark enough are returned unchanged.
 *
 * @param color - Brand color as hex string
 * @param maxBrightness - Perceived-brightness ceiling. The 170 default keeps
 *   brand colors recognizable; pass a lower value (~110) for small text or
 *   text sitting on a tint of the same color, where 170 is too washed out.
 * @returns A hex color guaranteed readable on white
 */
export function getReadableColorOnWhite(color: string, maxBrightness: number = 170): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  // Same YIQ perceived-brightness model as getContrastText. Anything brighter
  // than ~170 starts to wash out as text on white.
  const brightness = (r: number, g: number, b: number) => (r * 299 + g * 587 + b * 114) / 1000;

  let { r, g, b } = rgb;
  let safety = 0;
  while (brightness(r, g, b) > maxBrightness && safety < 20) {
    r = Math.max(0, Math.round(r * 0.85));
    g = Math.max(0, Math.round(g * 0.85));
    b = Math.max(0, Math.round(b * 0.85));
    safety++;
  }

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Make a brand color safe to use as a background behind white text.
 *
 * Very light brand colors (pastels, near-white) make white text illegible, so
 * darken them until white text reads clearly (same YIQ threshold as
 * getContrastText). Colors that are already dark enough are returned unchanged.
 *
 * Prefer this over getContrastText for surfaces whose design assumes white
 * foreground elements (e.g. the chatbot header with its white logo ring).
 *
 * @param color - Brand color as hex string
 * @returns A hex color guaranteed readable behind white text
 */
export function getWhiteTextSafeColor(color: string): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const brightness = (r: number, g: number, b: number) => (r * 299 + g * 587 + b * 114) / 1000;

  let { r, g, b } = rgb;
  let safety = 0;
  while (brightness(r, g, b) >= 150 && safety < 20) {
    r = Math.max(0, Math.round(r * 0.85));
    g = Math.max(0, Math.round(g * 0.85));
    b = Math.max(0, Math.round(b * 0.85));
    safety++;
  }

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Check if a color combination meets WCAG AA standards (4.5:1)
 * @param foreground - Foreground color hex
 * @param background - Background color hex
 * @returns true if combination meets AA standards
 */
export function meetsWCAGAA(foreground: string, background: string): boolean {
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);
  
  if (!fgRgb || !bgRgb) return false;
  
  const fgLuminance = getLuminance(fgRgb.r, fgRgb.g, fgRgb.b);
  const bgLuminance = getLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
  
  const contrast = getContrastRatio(fgLuminance, bgLuminance);
  
  return contrast >= 4.5;
}

/**
 * Lighten a color by a percentage
 * @param color - Hex color string
 * @param percent - Percentage to lighten (0-100)
 * @returns Lightened hex color
 */
export function lightenColor(color: string, percent: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  const amount = Math.round(2.55 * percent);
  const r = Math.min(255, rgb.r + amount);
  const g = Math.min(255, rgb.g + amount);
  const b = Math.min(255, rgb.b + amount);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Darken a color by a percentage
 * @param color - Hex color string
 * @param percent - Percentage to darken (0-100)
 * @returns Darkened hex color
 */
export function darkenColor(color: string, percent: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const amount = Math.round(2.55 * percent);
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Validate that a string is a valid hex color (#RGB or #RRGGBB)
 */
export function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color);
}

/**
 * Curated palette of accessible, visually distinct avatar background colors.
 * Each color is mid-saturation so white initials remain readable.
 */
const AVATAR_COLOR_PALETTE: string[] = [
  '#F97066', // coral red
  '#FB923C', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#22C55E', // green
  '#10B981', // emerald
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#F43F5E', // rose
];

/**
 * Deterministically pick an avatar background color for a given seed.
 * Use a stable identifier like the user's id (preferred) or full name so the
 * same person always renders with the same color.
 */
export function getAvatarColor(seed: string | null | undefined): string {
  const fallback = AVATAR_COLOR_PALETTE[0];
  if (!seed) return fallback;

  // djb2-style hash → stable, well-distributed across the palette
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % AVATAR_COLOR_PALETTE.length;
  return AVATAR_COLOR_PALETTE[index];
}

/**
 * Avatar colors for a group of known size, walked across the palette in even
 * strides so members never land on neighboring hues.
 *
 * Prefer this over `getAvatarColor` when a fixed set of people is rendered
 * side by side and telling them apart is the point (e.g. the providers on one
 * booking form): hashing is stable per user but can hand two teammates
 * near-identical blues.
 */
export function getSpacedAvatarColors(count: number): string[] {
  if (count <= 0) return [];

  const paletteSize = AVATAR_COLOR_PALETTE.length;
  const stride = count >= paletteSize ? 1 : Math.floor(paletteSize / count);
  return Array.from(
    { length: count },
    (_, i) => AVATAR_COLOR_PALETTE[(i * stride) % paletteSize]
  );
}

/**
 * Attempt to normalize a color string into valid #RRGGBB format.
 * - Adds '#' prefix if missing
 * - Expands 3-digit to 6-digit hex
 * - Returns null if the input cannot be normalized to a valid hex color
 */
export function normalizeHexColor(color: string): string | null {
  let cleaned = color.trim();

  if (!cleaned.startsWith('#')) {
    cleaned = '#' + cleaned;
  }

  if (/^#[A-Fa-f0-9]{3}$/.test(cleaned)) {
    const r = cleaned[1], g = cleaned[2], b = cleaned[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (/^#[A-Fa-f0-9]{6}$/.test(cleaned)) {
    return cleaned.toLowerCase();
  }

  return null;
}

